import React, { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  FaPhoneAlt,
  FaUserLock,
  FaSignOutAlt,
  FaCog,
  FaHandshake,
  FaChartBar,
  FaTaxi,
} from "react-icons/fa";
import { IoNavigate } from "react-icons/io5";
import { HiOutlineMail } from "react-icons/hi";
import {
  FaFacebookF,
  FaTwitter,
  FaLinkedinIn,
  FaInstagram,
} from "react-icons/fa";
import Logo from "../../assets/images/luxury-tours-luxury-transfers-black-leader-athens.png";

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isPartnerAuthenticated, setIsPartnerAuthenticated] = useState(false);
  const [isDriverAuthenticated, setIsDriverAuthenticated] = useState(false);
  const [isAdminMenuOpen, setIsAdminMenuOpen] = useState(false);
  const [isPartnerMenuOpen, setIsPartnerMenuOpen] = useState(false);
  const [isDriverMenuOpen, setIsDriverMenuOpen] = useState(false);
  const navigate = useNavigate();
  const adminDropdownRef = useRef(null);
  const partnerDropdownRef = useRef(null);
  const driverDropdownRef = useRef(null);

  useEffect(() => {
    // Check for admin, partner and driver tokens
    const adminToken = localStorage.getItem("Authentication:");
    const partnerToken = localStorage.getItem("partnerToken");
    const driverToken = localStorage.getItem("driverToken");
    setIsAuthenticated(!!adminToken);
    setIsPartnerAuthenticated(!!partnerToken);
    setIsDriverAuthenticated(!!driverToken);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        adminDropdownRef.current &&
        !adminDropdownRef.current.contains(event.target)
      ) {
        setIsAdminMenuOpen(false);
      }
      if (
        partnerDropdownRef.current &&
        !partnerDropdownRef.current.contains(event.target)
      ) {
        setIsPartnerMenuOpen(false);
      }
      if (
        driverDropdownRef.current &&
        !driverDropdownRef.current.contains(event.target)
      ) {
        setIsDriverMenuOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);
  const handlePartnerClick = () => {
    navigate("/partner/login");
  };

  const handlePartnerLogout = () => {
    localStorage.removeItem("partnerToken");
    setIsPartnerAuthenticated(false);
    setIsPartnerMenuOpen(false);
    navigate("/");
  };

  const handlePartnerDashboard = () => {
    setIsPartnerMenuOpen(false);
    navigate("/partner/dashboard");
  };

  const handleDriverClick = () => {
    navigate("/driver/login");
  };

  const handleDriverLogout = () => {
    localStorage.removeItem("driverToken");
    localStorage.removeItem("driverData");
    setIsDriverAuthenticated(false);
    setIsDriverMenuOpen(false);
    navigate("/");
  };

  const handleDriverDashboard = () => {
    setIsDriverMenuOpen(false);
    navigate("/driver/dashboard");
  };
  const handleAdminClick = () => {
    // Redirect to the external site
    navigate("/admin/login");
  };

  const handleLogout = async () => {
    try {
      localStorage.removeItem("adminToken");
      localStorage.removeItem("Authentication:");
      setIsAuthenticated(false);
      setIsAdminMenuOpen(false);
      navigate("/");
    } catch (error) {
      console.error("Logout failed:", error);
      localStorage.removeItem("adminToken");
      localStorage.removeItem("Authentication:");
      setIsAuthenticated(false);
      setIsAdminMenuOpen(false);
      navigate("/");
    }
  };

  const handleAdminPanel = () => {
    setIsAdminMenuOpen(false);
    navigate("/admin/dashboard");
  };

  const socialIcons = [
    { Icon: FaFacebookF, href: "/" },
    { Icon: FaTwitter, href: "/" },
    { Icon: FaLinkedinIn, href: "/" },
    { Icon: FaInstagram, href: "/" },
  ];

  const adminDropdownVariants = {
    hidden: {
      opacity: 0,
      y: 10,
      scale: 0.95,
    },
    visible: {
      opacity: 1,
      y: 0,
      scale: 1,
    },
  };

  const handleScrollToForm = () => {
    const formElement = document.getElementById("transfer-form");
    if (formElement) {
      formElement.scrollIntoView({ behavior: "smooth" });
    }
  };

  const handleScrollToTourForm = () => {
    const formElement = document.getElementById("tour-form");
    if (formElement) {
      formElement.scrollIntoView({ behavior: "smooth" });
    }
  }

  return (
    <header className={`w-full ${(window.location.pathname === '/' || window.location.pathname === '/partner/login' || window.location.pathname === '/driver/login')|| window.location.pathname === '/admin/login' ? 'absolute top-0 left-0 z-50 backdrop-blur-sm' : ''}`}>
      <motion.div
        className="w-full bg-[#f8f4f4]/80 border-b border-[#c7c7c7]"
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <div className="max-w-[1440px] mx-auto flex flex-col md:flex-row justify-between items-center gap-4 text-white py-2 px-4">
          <motion.div
            className="flex flex-row flex-wrap justify-center items-center text-center gap-1 xs:gap-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2, duration: 0.5 }}
          >
            {/* Contact Info Items */}
            <motion.a
              href="tel:+306932214626"
              className="flex flex-row items-center text-center gap-2 pr-3 border-r border-gray-200"
              whileHover={{ scale: 1.05 }}
            >
              <FaPhoneAlt className="text-md text-gray-600" />
              <span className="text-xs text-blue-900">+30 693 2214626</span>
            </motion.a>
            <motion.a
              href="mailto:blackleaderathens@gmail.com"
              className="flex flex-row items-center text-center gap-2 pl-2"
              whileHover={{ scale: 1.05 }}
            >
              <HiOutlineMail className="text-md text-gray-600  md:border-gray-200" />
              <span className="text-xs text-blue-900 md:border-r md:border-gray-200 mr-2 pr-3">blackleaderathens@gmail.com</span>
            </motion.a>
          </motion.div>

          <div className="flex items-center gap-4">

            {/* Admin Button or Dropdown */}
            <div className="relative" ref={adminDropdownRef}>
              {isAuthenticated ? (
                <div>
                  <motion.button
                    onClick={() => setIsAdminMenuOpen(!isAdminMenuOpen)}
                    className="flex items-center gap-2 px-3 py-1 text-sm text-blue-900 hover:text-red-600 transition-colors duration-300"
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    <FaUserLock className="text-md" />
                    <span className="hidden sm:inline">Admin</span>
                  </motion.button>

                  <AnimatePresence>
                    {isAdminMenuOpen && (
                      <motion.div
                        variants={adminDropdownVariants}
                        initial="hidden"
                        animate="visible"
                        exit="hidden"
                        className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-xl border border-gray-100 py-2 z-50"
                      >
                        <motion.button
                          onClick={handleAdminPanel}
                          className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-red-50 transition-colors duration-200"
                          whileHover={{ x: 4 }}
                        >
                          <FaCog className="mr-2" />
                          Admin Panel
                        </motion.button>
                        <motion.button
                          onClick={handleLogout}
                          className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-red-50 transition-colors duration-200"
                          whileHover={{ x: 4 }}
                        >
                          <FaSignOutAlt className="mr-2" />
                          Logout
                        </motion.button>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              ) : (
                <motion.button
                  onClick={handleAdminClick}
                  className="flex items-center gap-2 px-3 py-1 text-sm text-blue-900 hover:text-red-600 transition-colors duration-300"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <FaUserLock className="text-md" />
                  <span className="hidden sm:inline">Admin</span>
                </motion.button>
              )}
            </div>
            {/* Partner Login Button */}
            <div className="relative" ref={partnerDropdownRef}>
              {isPartnerAuthenticated ? (
                <div>
                  <motion.button
                    onClick={() => setIsPartnerMenuOpen(!isPartnerMenuOpen)}
                    className="flex items-center gap-2 px-3 py-1 text-sm text-blue-900 hover:text-red-600 transition-colors duration-300"
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    <FaHandshake className="text-md" />
                    <span className="hidden sm:inline">Partner</span>
                  </motion.button>

                  <AnimatePresence>
                    {isPartnerMenuOpen && (
                      <motion.div
                        variants={adminDropdownVariants}
                        initial="hidden"
                        animate="visible"
                        exit="hidden"
                        className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-xl border border-gray-100 py-2 z-50"
                      >
                        <motion.button
                          onClick={handlePartnerDashboard}
                          className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-blue-50 transition-colors duration-200"
                          whileHover={{ x: 4 }}
                        >
                          <FaChartBar className="mr-2" />
                          Dashboard
                        </motion.button>
                        <motion.button
                          onClick={handlePartnerLogout}
                          className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-red-50 transition-colors duration-200"
                          whileHover={{ x: 4 }}
                        >
                          <FaSignOutAlt className="mr-2" />
                          Logout
                        </motion.button>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              ) : (
                <motion.button
                  onClick={handlePartnerClick}
                  className="flex items-center gap-2 px-3 py-1 text-sm text-blue-900 hover:text-red-600 transition-colors duration-300"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <FaHandshake className="text-md" />
                  <span className="hidden sm:inline">Partner</span>
                </motion.button>
              )}
            </div>

            {/* Driver Login Button */}
            <div className="relative" ref={driverDropdownRef}>
              {isDriverAuthenticated ? (
                <div>
                  <motion.button
                    onClick={() => setIsDriverMenuOpen(!isDriverMenuOpen)}
                    className="flex items-center gap-2 px-3 py-1 text-sm text-blue-900 hover:text-red-600 transition-colors duration-300"
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    <FaTaxi className="text-md" />
                    <span className="hidden sm:inline">Driver</span>
                  </motion.button>

                  <AnimatePresence>
                    {isDriverMenuOpen && (
                      <motion.div
                        variants={adminDropdownVariants}
                        initial="hidden"
                        animate="visible"
                        exit="hidden"
                        className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-xl border border-gray-100 py-2 z-50"
                      >
                        <motion.button
                          onClick={handleDriverDashboard}
                          className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-blue-50 transition-colors duration-200"
                          whileHover={{ x: 4 }}
                        >
                          <FaChartBar className="mr-2" />
                          Dashboard
                        </motion.button>
                        <motion.button
                          onClick={handleDriverLogout}
                          className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-red-50 transition-colors duration-200"
                          whileHover={{ x: 4 }}
                        >
                          <FaSignOutAlt className="mr-2" />
                          Logout
                        </motion.button>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              ) : (
                <motion.button
                  onClick={handleDriverClick}
                  className="flex items-center gap-2 px-3 py-1 text-sm text-blue-900 hover:text-red-600 transition-colors duration-300"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <FaTaxi className="text-md" />
                  <span className="hidden sm:inline">Driver</span>
                </motion.button>
              )}
            </div>
          </div>
        </div>
      </motion.div>

      {/* Main Navigation */}
      <motion.nav
        className="w-full bg-transparent"
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.3 }}
      >
        <div className="max-w-[1440px] mx-auto px-4 backdrop-blur-sm">
          <div className="flex flex-row w-full h-full items-center justify-between py-4">
            <motion.a
              href="/"
              onClick={(e) => {
                e.preventDefault();
                navigate("/");
                window.scrollTo({ top: 0, behavior: "smooth" });
              }}
              className="cursor-pointer"
              whileHover={{ scale: 1.05 }}
            >
              <motion.img
                src={Logo}
                alt="Black Leader Transfers"
                className="h-12 sm:h-10 md:h-12 lg:h-20 lg:w-76 transition-all duration-300"
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5 }}
              />
            </motion.a>

            {/* Desktop Navigation - Only show on root path */}
            {window.location.pathname === '/' && (
              <div className="hidden lg:flex flex-row space-x-4 items-center">
                {/* Book Online Button */}
                <motion.button
                  onClick={handleScrollToForm}
                  className="bg-gradient-to-r from-black to-gray-700 text-white px-6 py-2 rounded-md hover:from-gray-800 hover:to-gray-600 transition-all duration-300"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  Make Booking
                </motion.button>

                {/* Call Now Button */}
                <motion.a
                  href="tel:+306932214626"
                  className="bg-gradient-to-r from-black to-gray-700 text-white px-6 py-2 rounded-md hover:from-gray-800 hover:to-gray-600 transition-all duration-300"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  Call Now
                </motion.a>
              </div>
            )}

            {/* Mobile Navigation Toggle */}
            <div className="lg:hidden flex items-center">
              <button
                className="text-gray-800 focus:outline-none"
                onClick={() => setIsMenuOpen(!isMenuOpen)}
              >
                <svg
                  className="h-6 w-6"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  {isMenuOpen ? (
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  ) : (
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M4 6h16M4 12h16M4 18h16"
                    />
                  )}
                </svg>
              </button>
            </div>
          </div>

          {/* Mobile Navigation Menu - Only show buttons on root path */}
          <AnimatePresence>
            {isMenuOpen && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
                exit={{ opacity: 0, height: 0 }}
                className="lg:hidden bg-white shadow-lg rounded-lg"
              >
                {window.location.pathname === '/' && (
                  <div className="flex flex-col py-4">
                    <div className="flex flex-col gap-2 px-4 pt-4">
                      <motion.button
                        onClick={() => {
                          handleScrollToForm();
                          setIsMenuOpen(false);
                        }}
                        className="bg-gradient-to-r from-black to-gray-700 text-white px-6 py-2 rounded-md hover:from-gray-800 hover:to-gray-600 transition-all duration-300"
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                      >
                        Make Booking
                      </motion.button>

                      <motion.a
                        href="tel:+306932214626"
                        className="bg-gradient-to-r from-black to-gray-700 text-white px-6 py-2 rounded-md hover:from-gray-800 hover:to-gray-600 transition-all duration-300 text-center"
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                      >
                        Call Now
                      </motion.a>
                    </div>
                  </div>
                )}
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </motion.nav>
    </header>
  );
};

export default Header;
